import { useEffect, useRef, useState } from "react";
import { Sheet } from "../../../shared/phone/Sheet";
import type { GoalConfig } from "../../../shared/goals/config";
import type { Need, ReadyLite } from "../../lib/ccApi";
import { NeedsList, type Decide } from "./Needs";

/** How long a decided card shows its answer before the next one slides in. */
const NEXT_MS = 900;

/**
 * What waits for the owner on a phone and an iPad, one thing at a time in the kit's sheet: today's need card,
 * then the next once it is decided, and the sheet closes after the last. `focus` opens it on that need.
 */
export function NeedSheet({ goalId, needs, config, ready, focus, review, onDecide, onClose }: { goalId: string; needs: Need[]; config: GoalConfig | null; ready: ReadyLite | null; focus: string | null; review: string; onDecide: Decide; onClose: () => void }) {
  const [cur, setCur] = useState<Need | null>(null);
  const deciding = useRef(false);
  const latest = useRef(needs);
  latest.current = needs;

  useEffect(() => {
    if (focus === null) {
      setCur(null);
      return;
    }
    // Why: while a decided card shows its answer it stays, though the list it came from has moved on.
    if (deciding.current) return;
    setCur((c) => (c && c.id === focus ? c : (needs.find((x) => x.id === focus) ?? c ?? needs[0] ?? null)));
  }, [focus, needs]);

  const decide: Decide = async (n, d) => {
    deciding.current = true;
    const ok = await onDecide(n, d);
    if (!ok) {
      deciding.current = false;
      return false;
    }
    setTimeout(() => {
      deciding.current = false;
      const next = latest.current.find((x) => x.id !== n.id) ?? null;
      if (next) setCur(next);
      else onClose();
    }, NEXT_MS);
    return true;
  };

  const at = cur ? needs.findIndex((x) => x.id === cur.id) : -1;
  return (
    <Sheet
      open={focus !== null}
      onClose={onClose}
      title="Needs you"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Close
        </button>
      }
      right={at >= 0 && needs.length > 1 ? <span className="gp-of">{`${at + 1} of ${needs.length}`}</span> : undefined}
    >
      <div className="ga gw ccx gp-in gp-needs">
        <NeedsList goalId={goalId} needs={cur ? [cur] : []} config={config} ready={ready} focus={null} review={review} onDecide={decide} />
      </div>
    </Sheet>
  );
}
